import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FilePlus2, Folder, Pin, Plus, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { ApiClientError } from "@/api/client";
import {
  fetchKbSection,
  fetchKbSectionDrafts,
  fetchKbSectionItems,
  fetchKbSectionItemsAll,
  KbListItem,
} from "@/api/knowledge";
import { KbCreatePageDialog } from "./KbCreatePageDialog";
import { KbFileUploadModal } from "./KbFileUploadModal";
import { KbFoldersModal } from "./KbFoldersModal";
import {
  KbDraftChip,
  KbErrorBanner,
  KbErrorCard,
  KbNotFound,
  KbRow,
  KbRowsSkeleton,
  KbSecTitle,
  KbTilesSkeleton,
  kbPublishedRight,
  kbTypeSuffix,
} from "./kbUi";

function formatKbShortDateTime(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" }) +
    " о " +
    d.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })
  );
}

/**
 * D-147 W1 S3 — екран розділу (visual package v1.2 LOCK, екран ②,
 * reader-зріз): crumbs · мета (Власник · Резерв · N опублікованих) ·
 * «Закріплені» · «Папки» (клік = фільтр матеріалів) · «Матеріали».
 * Editor+-поверхні («+ Сторінка»/«+ Файл»/«Керувати папками»/«Чернетки
 * розділу»/unpin) — S4/S5.
 *
 * P12: прямий URL чужого/неіснуючого розділу → generic-404 «Розділ
 * недоступний або не існує» (backend 404 resource_not_found).
 */
export function KnowledgeSectionPage() {
  const { sectionId } = useParams<{ sectionId: string }>();
  const id = sectionId ?? "";
  const [folderFilter, setFolderFilter] = useState<string | null>(null);
  const [createPageOpen, setCreatePageOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const section = useQuery({
    queryKey: ["kbSection", id],
    queryFn: ({ signal }) => fetchKbSection(id, signal),
    enabled: !!id,
    staleTime: 30_000,
    retry: (count, e) => !(e instanceof ApiClientError && e.status === 404) && count < 2,
  });

  const editorGrade = ["editor", "owner", "admin"].includes(section.data?.capability ?? "");

  // S4: editor+ отримує scope=all (draft-стани, AC-KB-05: reader цих ключів не має)
  const items = useQuery({
    queryKey: ["kbSectionItems", id, editorGrade ? "all" : "published"],
    queryFn: ({ signal }) =>
      editorGrade ? fetchKbSectionItemsAll(id, signal) : fetchKbSectionItems(id, signal),
    enabled: !!id && section.isSuccess,
    staleTime: 30_000,
  });

  const drafts = useQuery({
    queryKey: ["kbSectionDrafts", id],
    queryFn: ({ signal }) => fetchKbSectionDrafts(id, signal),
    enabled: !!id && editorGrade,
    staleTime: 30_000,
  });

  const notFound = section.error instanceof ApiClientError && section.error.status === 404;

  const folderName = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of section.data?.folders ?? []) map.set(f.id, f.name);
    return map;
  }, [section.data?.folders]);

  // scope=all містить draft-only/archived — публічні поверхні беруть ЛИШЕ published
  const rawItems = items.data?.items ?? [];
  const allItems = rawItems.filter((i) => i.status === undefined || i.status === "published");
  const pinned = allItems.filter((i) => i.isPinned);
  const regular = allItems.filter(
    (i) => !i.isPinned && (folderFilter === null || i.folderId === folderFilter),
  );
  const publishedTotal = useMemo(
    () =>
      Object.values(section.data?.folderCounts ?? {}).reduce(
        (sum, c) => sum + (c.publishedCount ?? 0),
        0,
      ),
    [section.data?.folderCounts],
  );

  if (notFound) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto max-w-[1080px]">
          <KbNotFound text="Розділ недоступний або не існує" />
        </div>
      </div>
    );
  }

  const rowSub = (i: KbListItem) => {
    const parts: string[] = [];
    const fn = i.folderId ? folderName.get(i.folderId) : undefined;
    if (fn) parts.push(fn);
    if (i.type === "file") parts.push(kbTypeSuffix("file", i.originalFilename, i.fileSize));
    else if (i.owner) parts.push(`відповідальний: ${i.owner.name}`);
    return parts.join(" · ");
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-[1080px]">
        {section.isPending ? (
          <>
            <KbRowsSkeleton rows={1} />
            <KbSecTitle>Папки</KbSecTitle>
            <KbTilesSkeleton />
            <KbSecTitle>Матеріали</KbSecTitle>
            <KbRowsSkeleton />
          </>
        ) : section.isError && !section.data ? (
          <KbErrorCard text="Не вдалося завантажити розділ" onRetry={() => void section.refetch()} />
        ) : section.data ? (
          <>
            {(section.isError || items.isError) && (
              <KbErrorBanner
                text="Не вдалося оновити розділ — показано попередні дані."
                onRetry={() => {
                  void section.refetch();
                  void items.refetch();
                }}
              />
            )}

            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <nav className="mb-1 text-xs text-muted-foreground" aria-label="Хлібні крихти">
                  <Link to="/knowledge" className="text-primary hover:underline">
                    База знань
                  </Link>
                  <span> / {section.data.name}</span>
                </nav>
                <h1 className="text-xl font-semibold tracking-tight">{section.data.name}</h1>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {[
                    section.data.owner ? `Власник: ${section.data.owner.name}` : null,
                    section.data.backupOwner ? `Резерв: ${section.data.backupOwner.name}` : null,
                    `${publishedTotal} опублікованих`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              {/* S4: create — ЛИШЕ з розділу; видимість = backend sectionActions (P2) */}
              <div className="flex flex-wrap items-center gap-2">
                {section.data.sectionActions.createPage && (
                  <button
                    type="button"
                    onClick={() => setCreatePageOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border px-3.5 py-2 text-sm font-semibold hover:bg-accent"
                  >
                    <Plus className="h-4 w-4" aria-hidden /> Сторінка
                  </button>
                )}
                {section.data.sectionActions.createFile && (
                  <button
                    type="button"
                    onClick={() => setUploadOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border px-3.5 py-2 text-sm font-semibold hover:bg-accent"
                  >
                    <FilePlus2 className="h-4 w-4" aria-hidden /> Файл
                  </button>
                )}
                {section.data.sectionActions.manageFolders && (
                  <button
                    type="button"
                    onClick={() => setFoldersOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border px-3.5 py-2 text-sm font-semibold hover:bg-accent"
                  >
                    <Folder className="h-4 w-4" aria-hidden /> Керувати папками
                  </button>
                )}
              </div>
            </div>
            {editorGrade && (
              <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="h-3.5 w-3.5 accent-primary"
                />
                Показати архівовані
              </label>
            )}

            {items.isPending ? (
              <>
                <KbSecTitle>Матеріали</KbSecTitle>
                <KbRowsSkeleton />
              </>
            ) : (
              <>
                {pinned.length > 0 && (
                  <>
                    <KbSecTitle>
                      <Pin className="h-3.5 w-3.5" aria-hidden /> Закріплені
                    </KbSecTitle>
                    <div className="overflow-hidden rounded-xl border bg-card">
                      {pinned.map((i) => (
                        <KbRow
                          key={i.itemId}
                          to={`/knowledge/items/${i.itemId}`}
                          type={i.type}
                          title={i.title}
                          sub={rowSub(i)}
                          right={kbPublishedRight(i.lastPublishedAt)}
                        />
                      ))}
                    </div>
                  </>
                )}

                {(section.data.folders.length > 0 || allItems.length > 0) && (
                  <>
                    {section.data.folders.length > 0 && (
                      <>
                        <KbSecTitle>Папки</KbSecTitle>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                          {section.data.folders.map((f) => {
                            const selected = folderFilter === f.id;
                            return (
                              <button
                                key={f.id}
                                type="button"
                                onClick={() => setFolderFilter(selected ? null : f.id)}
                                aria-pressed={selected}
                                className={cn(
                                  "rounded-xl border bg-card p-3.5 text-left transition-colors hover:border-primary focus-visible:border-primary focus-visible:outline-none",
                                  selected && "border-primary ring-1 ring-primary",
                                )}
                              >
                                <span className="flex items-center gap-1.5 text-sm font-bold">
                                  <Folder className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
                                  {f.name}
                                </span>
                                <span className="mt-2 block text-xs text-muted-foreground">
                                  {section.data.folderCounts[f.id]?.publishedCount ?? 0} опублікованих
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}

                    <KbSecTitle>Матеріали</KbSecTitle>
                    {folderFilter !== null && (
                      <button
                        type="button"
                        onClick={() => setFolderFilter(null)}
                        className="mb-2.5 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                      >
                        Папка: {folderName.get(folderFilter) ?? ""} <X className="h-3 w-3" aria-hidden />
                      </button>
                    )}
                    {regular.length === 0 ? (
                      <div className="rounded-2xl border border-dashed bg-card px-4 py-10 text-center text-sm">
                        {folderFilter !== null ? (
                          <span className="font-medium">У цій папці поки немає матеріалів</span>
                        ) : allItems.length === 0 ? (
                          <span className="font-medium">У цьому розділі поки немає матеріалів</span>
                        ) : (
                          <span className="font-medium">Усі матеріали закріплені вище</span>
                        )}
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded-xl border bg-card">
                        {regular.map((i) => (
                          <KbRow
                            key={i.itemId}
                            to={`/knowledge/items/${i.itemId}`}
                            type={i.type}
                            title={i.title}
                            sub={rowSub(i)}
                            right={kbPublishedRight(i.lastPublishedAt)}
                            chip={
                              i.hasUnpublishedChanges ? (
                                <KbDraftChip>є неопубліковані зміни</KbDraftChip>
                              ) : undefined
                            }
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}

                {editorGrade && (drafts.data?.items.length ?? 0) > 0 && (
                  <>
                    <KbSecTitle>Чернетки розділу</KbSecTitle>
                    <div className="overflow-hidden rounded-xl border bg-card">
                      {drafts.data!.items.map((d) => (
                        <KbRow
                          key={d.itemId}
                          to={`/knowledge/items/${d.itemId}?view=draft`}
                          type={d.type}
                          title={d.draftTitle || d.publishedTitle}
                          sub={[
                            d.ownerName ? `відповідальний: ${d.ownerName}` : null,
                            `остання зміна: ${d.draftModifiedByName ? d.draftModifiedByName + ", " : ""}${formatKbShortDateTime(d.draftModifiedAt)}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          chip={<KbDraftChip>чернетка</KbDraftChip>}
                        />
                      ))}
                    </div>
                  </>
                )}

                {editorGrade && showArchived && (
                  <>
                    <KbSecTitle>Архівовані</KbSecTitle>
                    {rawItems.filter((i) => i.status === "archived").length === 0 ? (
                      <div className="rounded-2xl border border-dashed bg-card px-4 py-8 text-center text-sm text-muted-foreground">
                        Архівованих матеріалів немає
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded-xl border bg-card">
                        {rawItems
                          .filter((i) => i.status === "archived")
                          .map((i) => (
                            <KbRow
                              key={i.itemId}
                              to={`/knowledge/items/${i.itemId}`}
                              type={i.type}
                              title={i.title || i.draftTitle || ""}
                              sub="відновлюється як чернетка"
                              chip={
                                <span className="inline-flex items-center rounded-full border bg-muted px-2 py-px text-[11px] font-semibold text-muted-foreground">
                                  архів
                                </span>
                              }
                            />
                          ))}
                      </div>
                    )}
                  </>
                )}

                {section.data.folders.length === 0 && allItems.length === 0 && (
                  <div className="mt-6 rounded-2xl border border-dashed bg-card px-4 py-12 text-center">
                    <div className="text-sm font-medium">У цьому розділі поки немає матеріалів</div>
                  </div>
                )}
              </>
            )}

            {createPageOpen && (
              <KbCreatePageDialog
                sectionId={id}
                folders={section.data.folders}
                onClose={() => setCreatePageOpen(false)}
              />
            )}
            {foldersOpen && (
              <KbFoldersModal
                sectionId={id}
                folders={section.data.folders}
                onClose={() => setFoldersOpen(false)}
              />
            )}
            {uploadOpen && (
              <KbFileUploadModal
                sectionId={id}
                sectionName={section.data.name}
                ownerName={section.data.owner?.name ?? ""}
                ownerGrade={
                  section.data.capability === "owner" || section.data.capability === "admin"
                }
                folders={section.data.folders}
                onClose={() => setUploadOpen(false)}
              />
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
